import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalDialog,
    ModalHeader,
    Button,
} from 'arag-ui-lib';
import { useTermsAndConditions } from '../../hooks/useTermsAndConditions';
import styles from './termsWithLanguageModal.module.scss';
import { RootState } from '../../store/store';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../actions';
import LanguageIcon from '../icons/languageIcon';
import codes from '../../utils/constants/codes';

type Props = {
    areTermsConsented: boolean;
    isModalOpen: boolean;
};

const TermsWithLanguageModal = (props: Props): React.ReactElement => {
    const { isModalOpen, areTermsConsented } = props;

    const dispatch = useDispatch();
    const [userHasReviewed, setUserHasReviewed] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>('en-GB'); // Default to English

    const currentCountry = useSelector(
        (state: RootState) =>
            state.applicationSettings.apiApplicationSettings.currentCountryCode,
    );
    const userId = useSelector(
        (state: RootState) => state.applicationUser.applicationUserDetails.id,
    );
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Detect browser language on mount
    useEffect(() => {
        const lang = navigator.language;
        setSelectedLanguage(lang);
    }, []);

    // Fetch terms only when showTerms is true and a language is selected
    const { data: termsAndConditions } = useTermsAndConditions(
        areTermsConsented,
        currentCountry,
        userId,
        selectedLanguage,
    );

    const updateApplicationTermsAndConditionsTermsConsentedAction = useCallback(
        (payload: any) =>
            dispatch(
                actions.updateApplicationTermsAndConditionsTermsConsented(
                    payload,
                ),
            ),
        [dispatch],
    );

    // Reset and check if scrolling is needed when terms content changes
    useEffect(() => {
        if (!showTerms || !termsAndConditions) return;

        setUserHasReviewed(false);

        // Wait for content to render, then check if scrolling is needed
        const timer = setTimeout(() => {
            const el = scrollContainerRef.current;
            if (el && el.scrollHeight <= el.clientHeight) {
                setUserHasReviewed(true);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [showTerms, termsAndConditions]);

    const scrollTracker = (event: any) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;
        if (scrollHeight <= clientHeight) {
            setUserHasReviewed(true);
            return;
        }
        const position = Math.ceil(
            (scrollTop / (scrollHeight - clientHeight)) * 100,
        );
        if (position >= 98) {
            setUserHasReviewed(true);
        }
    };

    const handleAgreeClick = () => {
        updateApplicationTermsAndConditionsTermsConsentedAction({
            userId: userId,
            languageCode: selectedLanguage,
        });
    };

    const handleLanguageSelect = (lang: string) => {
        setSelectedLanguage(lang);
        setShowTerms(true);
    };

    return (
        <>
            {!areTermsConsented && termsAndConditions !== null ? (
                <>
                    <style>
                        {`
                #display_termsModalWithLanguage_header_close {
                    display: none;
                }
                `}
                    </style>
                    <div className={styles['modal-wrapper']}>
                        <Modal
                            id={'display_termsModalWithLanguage_modal'}
                            fadeIn={isModalOpen}
                            darkenedBackground
                            noScroll={true}
                        >
                            <ModalDialog
                                id={'display_termsModalWithLanguage_dialog'}
                                size={!showTerms ? 'small' : 'lg'}
                            >
                                <ModalContent
                                    id={
                                        'display_termsModalWithLanguage_content'
                                    }
                                >
                                    <ModalHeader
                                        id={
                                            'display_termsModalWithLanguage_header'
                                        }
                                        title={''}
                                    />
                                    <ModalBody
                                        id={
                                            'display_termsModalWithLanguage_wrapper'
                                        }
                                    >
                                        {!showTerms ? (
                                            // Language detection and selection section
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    background: '#f5f5f5',
                                                    borderRadius: 12,
                                                    padding: 24,
                                                    marginBottom: 24,
                                                }}
                                            >
                                                <LanguageIcon
                                                    width={48}
                                                    height={48}
                                                    stroke="#000"
                                                    style={{ marginBottom: 12 }}
                                                />
                                                <div
                                                    style={{
                                                        fontWeight: 'bold',
                                                        fontSize: 18,
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: termsAndConditions?.languageModalTitle,
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        marginBottom: 16,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: termsAndConditions?.languageModalBody,
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        gap: 16,
                                                    }}
                                                >
                                                    <Button
                                                        variant="primary"
                                                        label={
                                                            termsAndConditions?.languageModalButtonContinueText
                                                        }
                                                        onClick={() =>
                                                            handleLanguageSelect(
                                                                selectedLanguage ==
                                                                    codes.languageFrCaCode
                                                                    ? selectedLanguage
                                                                    : codes.languageEnCaCode,
                                                            )
                                                        }
                                                    />
                                                    <Button
                                                        variant="primary"
                                                        label={
                                                            termsAndConditions?.languageModalButtonSwitchText
                                                        }
                                                        onClick={() =>
                                                            handleLanguageSelect(
                                                                selectedLanguage ===
                                                                    codes.languageEnCaCode ||
                                                                    selectedLanguage !==
                                                                        codes.languageFrCaCode
                                                                    ? codes.languageFrCaCode
                                                                    : codes.languageEnCaCode,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            // Terms and Conditions section
                                            termsAndConditions && (
                                                <>
                                                    <div
                                                        className={
                                                            styles['text']
                                                        }
                                                        onScroll={scrollTracker}
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: termsAndConditions?.title,
                                                            }}
                                                        />
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: termsAndConditions?.body,
                                                            }}
                                                        />
                                                    </div>
                                                    <br />
                                                    <br />
                                                    <div
                                                        className={
                                                            styles[
                                                                'modal-footer-button'
                                                            ]
                                                        }
                                                    >
                                                        <Button
                                                            variant="primary"
                                                            label={
                                                                termsAndConditions?.agreeButtonText
                                                            }
                                                            type={'submit'}
                                                            disabled={
                                                                !userHasReviewed
                                                            }
                                                            arrowDirection="right"
                                                            onClick={
                                                                handleAgreeClick
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            )
                                        )}
                                    </ModalBody>
                                </ModalContent>
                            </ModalDialog>
                        </Modal>
                    </div>
                </>
            ) : null}
        </>
    );
};

export default TermsWithLanguageModal;
